import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin, QueryClient } from '@tanstack/vue-query'
import PrimeVue from 'primevue/config'
import ConfirmationService from 'primevue/confirmationservice'
import { GesturePlugin } from '@vueuse/gesture'
import Aura from '@primeuix/themes/aura'
import router, { configureRouterToast } from '@dashboard/router'
import { notificationToastService } from '@dashboard/services/notificationToastService'

import App from '@dashboard/App.vue'
import '@fontsource-variable/figtree/index.css'
import 'primeicons/primeicons.css'
import './assets/css/app.css'
import './assets/css/validation.css'
import '@shared/styles/action-button.css'
import Ripple from 'primevue/ripple'
import Tooltip from 'primevue/tooltip'
import 'iconify-icon'
import { initializeRealtimeConnectionMonitoring } from './services/pusher'
import { initializeSentry } from './services/sentry'
import { isModuleLoadFailure, useSystemStore } from '@dashboard/stores/systemStore'

declare module 'vue' {
  interface GlobalDirectives {
    ripple: typeof Ripple
    tooltip: typeof Tooltip
  }
}

const pinia = createPinia()
const app = createApp(App)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      throwOnError: false
    }
  }
})
initializeSentry(app, router)

app.use(pinia)
initializeRealtimeConnectionMonitoring()

window.addEventListener('unhandledrejection', (event) => {
  if (isModuleLoadFailure(event.reason)) {
    void useSystemStore().handleAssetLoadFailure()
  }
})

app.use(VueQueryPlugin, { queryClient })
app.use(PrimeVue, {
  license: import.meta.env.VITE_PRIMEUI_LICENSE_KEY,
  theme: {
    preset: Aura,
    options: {
      darkModeSelector: '.dark',
      cssLayer: {
        name: 'primeui',
        order: 'theme, tailwind-base, base, primeui, components, tailwind-utilities'
      }
    }
  }
})
app.use(ConfirmationService)
app.use(GesturePlugin)
app.use(notificationToastService)
configureRouterToast(app.config.globalProperties.$toast)
app.use(router)
app.directive('ripple', Ripple)
app.directive('tooltip', Tooltip)
app.mount('#app')

const systemStore = useSystemStore()
systemStore.initSystemChannel()
